import { useState, useEffect } from 'react';
import { getUsers, createUser, deleteUser, updateUser, type User } from '../../api/users.api';
import { useAuth } from '../../context/AuthContext';

export default function Usuarios() {
  const { user: currentUser } = useAuth();
  // Multi sede: el ADMIN asigna a cada operador la sede en la que trabaja
  const sedes = currentUser?.rol === 'ADMIN' ? currentUser.sedes ?? [] : [];
  const defaultSedeId = sedes.length === 1 ? sedes[0].id : '';
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [filterRol, setFilterRol] = useState('TODOS');
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    rol: 'OPERADOR' as 'ADMIN' | 'OPERADOR',
    telefono: '',
    password: '',
    sedeId: defaultSedeId
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (user: User) => {
    setFormData({
        nombre: user.nombre,
        email: user.email,
        rol: user.rol === 'SUPERADMIN' ? 'ADMIN' : user.rol as 'ADMIN' | 'OPERADOR', // Handle SUPERADMIN edge case if needed, or just map directly
        telefono: user.telefono || '',
        password: '', // Don't populate password
        sedeId: user.sedeId || defaultSedeId
    });
    setEditingId(user.id);
    setShowModal(true);
  };

  const handleCloseModal = () => {
      setShowModal(false);
      setEditingId(null);
      setFormData({
        nombre: '',
        email: '',
        rol: 'OPERADOR',
        telefono: '',
        password: '',
        sedeId: defaultSedeId
      });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { sedeId, ...data } = formData;
      // La sede solo aplica a operadores (y solo la gestiona el ADMIN de la empresa)
      const sedePayload = sedes.length > 0 && formData.rol === 'OPERADOR' && sedeId ? { sedeId } : {};
      if (editingId) {
          const updateData: any = { ...data, ...sedePayload };
          if (!updateData.password) delete updateData.password;
          await updateUser(editingId, updateData);
      } else {
          await createUser({ ...data, ...sedePayload });
      }
      await loadUsers();
      handleCloseModal();
    } catch (error: any) {
      console.error('Error saving user:', error);
      const errorMessage = error?.response?.data?.message || 'Error al guardar usuario. Verifique los datos o permisos.';
      alert(Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if(!confirm('¿Estás seguro de eliminar este usuario?')) return;
    try {
      await deleteUser(id);
      await loadUsers();
    } catch (error: any) {
      console.error('Error removing user:', error);
      alert(error?.response?.data?.message || 'Error eliminando usuario.');
    }
  };

  const filteredUsuarios = users.filter(u => {
    if (filterRol === 'TODOS') return true;
    return u.rol === filterRol;
  });

  const getRolColor = (rol: string) => {
    switch (rol) {
      case 'SUPERADMIN': return 'bg-purple-100 text-purple-700';
      case 'ADMIN': return 'bg-blue-100 text-blue-700';
      case 'OPERADOR': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <>
      <div className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Usuarios</h1>
            <p className="text-slate-500 text-sm mt-1">Gestión de usuarios del sistema</p>
          </div>
          <button
            onClick={() => {
                handleCloseModal(); // Reset form ensuring clear state
                setShowModal(true);
            }}
            className="bg-teal-500 hover:bg-teal-600 text-white px-4 py-2.5 rounded-xl flex items-center justify-center gap-2 transition whitespace-nowrap w-full sm:w-auto"
          >
            <i className="ri-user-add-line text-lg"></i>
            <span>Nuevo Usuario</span>
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-6 overflow-x-auto">
            <div className="flex gap-2 pb-2 sm:pb-0">
              {['TODOS', 'ADMIN', 'OPERADOR'].map(rol => (
                <button
                  key={rol}
                  onClick={() => setFilterRol(rol)}
                  className={`px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-medium transition whitespace-nowrap ${
                    filterRol === rol
                      ? 'bg-teal-500 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {rol}
                </button>
              ))}
            </div>
          </div>

          {/* Vista de tabla para desktop */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Usuario</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Rol</th>
                  {sedes.length > 1 && <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Sede</th>}
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Teléfono</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Estado</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Último Acceso</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={sedes.length > 1 ? 8 : 7} className="text-center py-8 text-slate-500">Cargando usuarios...</td></tr>
                ) : filteredUsuarios.length === 0 ? (
                  <tr><td colSpan={sedes.length > 1 ? 8 : 7} className="text-center py-8 text-slate-500">No se encontraron usuarios.</td></tr>
                ) : (
                  filteredUsuarios.map(usuario => (
                  <tr key={usuario.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                          <i className="ri-user-line text-teal-600"></i>
                        </div>
                        <span className="font-semibold text-slate-800">{usuario.nombre}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700">{usuario.email}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${getRolColor(usuario.rol)}`}>
                        {usuario.rol}
                      </span>
                    </td>
                    {sedes.length > 1 && <td className="py-3 px-4 text-sm text-slate-700">{usuario.sede?.nombre || '-'}</td>}
                    <td className="py-3 px-4 text-sm text-slate-700">{usuario.telefono || '-'}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
                        usuario.status === 'ACTIVO' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {usuario.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700">{usuario.ultimoAcceso ? new Date(usuario.ultimoAcceso).toLocaleDateString() : 'Nunca'}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <button 
                            onClick={() => handleEdit(usuario)}
                            className="w-8 h-8 flex items-center justify-center text-slate-600 hover:bg-slate-100 rounded-lg transition cursor-pointer"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button 
                          onClick={() => handleDelete(usuario.id)}
                          className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition cursor-pointer"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Vista de tarjetas para móvil */}
          <div className="md:hidden space-y-3">
            {isLoading ? (
              <div className="text-center py-8 text-slate-500">Cargando usuarios...</div>
            ) : filteredUsuarios.length === 0 ? (
              <div className="text-center py-8 text-slate-500">No se encontraron usuarios.</div>
            ) : (
              filteredUsuarios.map(usuario => (
                <div key={usuario.id} className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-line text-teal-600 text-xl"></i>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-800 truncate">{usuario.nombre}</p>
                        <p className="text-xs text-slate-500 truncate">{usuario.email}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button 
                        onClick={() => handleEdit(usuario)}
                        className="w-8 h-8 flex items-center justify-center text-slate-600 hover:bg-white rounded-lg transition"
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                      <button 
                        onClick={() => handleDelete(usuario.id)}
                        className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-xs text-slate-500">Rol</p>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium mt-1 ${getRolColor(usuario.rol)}`}>
                        {usuario.rol}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Estado</p>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium mt-1 ${
                        usuario.status === 'ACTIVO' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {usuario.status}
                      </span>
                    </div>
                    {usuario.telefono && (
                      <div className="col-span-2">
                        <p className="text-xs text-slate-500">Teléfono</p>
                        <p className="text-sm font-medium text-slate-700">{usuario.telefono}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-800">{editingId ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
              <button
                onClick={handleCloseModal}
                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Nombre Completo *</label>
                  <input
                    type="text"
                    value={formData.nombre}
                    maxLength={50}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]*$/.test(val)) {
                            setFormData({ ...formData, nombre: val });
                        }
                    }}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                    placeholder="Juan Pérez"
                    required
                  />
                  <p className="text-xs text-slate-400 mt-1">Solo caracteres alfabéticos.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    maxLength={50}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                    placeholder="usuario@ejemplo.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Teléfono *</label>
                  <input
                    type="tel"
                    value={formData.telefono}
                    maxLength={13}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (/^[0-9]*$/.test(val)) {
                            setFormData({ ...formData, telefono: val });
                        }
                    }}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                    placeholder="3001234567"
                    required
                  />
                  <p className="text-xs text-slate-400 mt-1">Solo números, máximo 13 dígitos.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Rol *</label>
                  <select
                    value={formData.rol}
                    onChange={(e) => setFormData({ ...formData, rol: e.target.value as any })}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm pr-8"
                    required
                  >
                    <option value="OPERADOR">Operador</option>
                    <option value="ADMIN">Administrador</option>
                  </select>
                </div>
                {sedes.length > 1 && formData.rol === 'OPERADOR' && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Sede *</label>
                    <select
                      value={formData.sedeId}
                      onChange={(e) => setFormData({ ...formData, sedeId: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm pr-8"
                      required
                    >
                      <option value="">Seleccione la sede del operador</option>
                      {sedes.map((sede) => (
                        <option key={sede.id} value={sede.id}>{sede.nombre}</option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-400 mt-1">El operador solo podrá trabajar en esta sede.</p>
                  </div>
                )}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Contraseña {editingId && '(Dejar en blanco para mantener)'} *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                    placeholder="••••••••"
                    required={!editingId}
                    minLength={6}
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition whitespace-nowrap"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2.5 bg-teal-500 hover:bg-teal-600 text-white rounded-xl transition whitespace-nowrap flex justify-center gap-2"
                >
                  {isSubmitting ? <i className="ri-loader-4-line animate-spin"></i> : 'Crear Usuario'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
